import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'durationSelect',
    'elapsedLabel',
    'playButton',
    'progress',
    'progressBar',
    'stopButton',
    'timeInput'
  ]

  static values = {
    completeTone: { type: Boolean, default: true }
  }

  connect () {
    this._animationFrame = null
    this._completed = false
    this._elapsed = 0
    this._timer = null
    this._startedAt = null
    this._targetSeconds = 0
  }

  disconnect () {
    this._stopTimer()
  }

  start () {
    if (this._timer) return

    this._elapsed = 0
    this._completed = false
    this._startedAt = Date.now()
    this._targetSeconds = this._selectedMinutes() * 60
    this._setRunning(true)
    this._startProgress()
    this._tick()
    this._timer = setInterval(() => this._tick(), 1000)
  }

  stop () {
    this._complete(false)
  }

  _complete (playTone) {
    if (this._completed) return

    this._completed = true
    this._refreshElapsed()
    this._stopTimer()
    this._syncTimeInput()
    this.durationSelectTarget.disabled = false

    if (playTone && this.completeToneValue) this._playTone()

    this.element.requestSubmit()
  }

  _formatElapsed () {
    const minutes = Math.floor(this._elapsed / 60)
    const seconds = this._elapsed % 60

    if (minutes <= 0) return `${seconds}s`
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`
  }

  _playTone () {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return

    const context = new AudioContext()
    const gain = context.createGain()
    const oscillator = context.createOscillator()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(440, context.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(660, context.currentTime + 0.7)
    gain.gain.setValueAtTime(0.001, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.05)
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 1.2)

    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start()
    oscillator.stop(context.currentTime + 1.2)
  }

  _selectedMinutes () {
    return Number.parseInt(this.durationSelectTarget.value || '0', 10)
  }

  _setRunning (running) {
    this.durationSelectTarget.disabled = running
    this.durationSelectTarget.classList.toggle('hidden', running)
    this.playButtonTarget.classList.toggle('hidden', running)
    this.stopButtonTarget.classList.toggle('hidden', !running)
    this.elapsedLabelTarget.classList.toggle('hidden', !running)
    this.progressTarget.classList.toggle('hidden', !running || this._targetSeconds <= 0)
  }

  _startProgress () {
    this.progressBarTarget.style.width = '0%'

    if (this._targetSeconds <= 0) return

    this._animationFrame = window.requestAnimationFrame(() => this._updateProgress())
  }

  _stopTimer () {
    clearInterval(this._timer)
    window.cancelAnimationFrame(this._animationFrame)
    this._animationFrame = null
    this._timer = null
  }

  _syncTimeInput () {
    this.timeInputTarget.value = this._elapsed
  }

  _tick () {
    this._refreshElapsed()
    this._syncTimeInput()

    if (this._targetSeconds > 0) {
      this.elapsedLabelTarget.textContent = `${this._selectedMinutes()}m set`
    } else {
      this.elapsedLabelTarget.textContent = this._formatElapsed()
    }
  }

  _updateProgress () {
    if (this._completed) return

    const elapsedMs = Date.now() - this._startedAt
    const targetMs = this._targetSeconds * 1000
    const percent = Math.min(100, (elapsedMs / targetMs) * 100)

    this.progressBarTarget.style.width = `${percent}%`

    if (elapsedMs >= targetMs) {
      this._complete(true)
    } else {
      this._animationFrame = window.requestAnimationFrame(() => this._updateProgress())
    }
  }

  _refreshElapsed () {
    this._elapsed = Math.max(1, Math.floor((Date.now() - this._startedAt) / 1000))
  }
}
